/**
 * Print-Only Business Gap Assessment Page for PDF Generation
 * Includes View Summary content, category scores, charts, and analysis
 *
 * URL: /print/business-gap-assessment/:businessId
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import '../../print.css';

const PrintBusinessGapAssessment = () => {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter (for PDF generation) or localStorage (for direct viewing)
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };


        // Fetch business profile, assessment, and summary in parallel
        const [businessRes, assessmentRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/assessment/current`, { headers })
        ]);

        if (!businessRes.ok || !assessmentRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const businessData = await businessRes.json();
        const assessmentData = await assessmentRes.json();

        // Fetch summary if we have an assessment
        let summaryData = null;
        if (assessmentData.id) {
          const summaryRes = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/assessment/${assessmentData.id}/summary`, { headers });
          if (summaryRes.ok) {
            summaryData = await summaryRes.json();
          }
        }

        setData({
          business: businessData.data.profile,
          assessment: assessmentData,
          summary: summaryData
        });

        setLoading(false);
      } catch (err) {
        console.error('[PrintBusinessGapAssessment] Error loading data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  useEffect(() => {
    if (!loading && data) {
      document.body.setAttribute('data-pdf-ready', 'true');

      const businessName = data?.business?.business_name || 'Business';
      document.title = `${businessName} - Business Gap Assessment Report`;
    }
  }, [loading, data]);

  // Extract data (before any conditional returns to avoid hook issues)
  const { business, assessment, summary } = data || {};
  const answeredQuestions = assessment?.responses || [];

  // Activity Type and Asset Type Mappings (constants can be defined here)
  const activityTypeMapping = {
    'De-Risking Activity': ['LEGAL-002', 'OWNER-001', 'CUST-003', 'LEGAL-005', 'FIN-007', 'IP-004', 'MGT-005', 'OWNER-004', 'OWNER-006', 'LEGAL-008', 'LEGAL-007', 'IP-007', 'FIN-002', 'IP-001', 'FIN-005', 'IP-002', 'FIN-009', 'MGT-010', 'CUST-002', 'LEGAL-003', 'OWNER-007', 'REV-001', 'OWNER-005', 'OWNER-010', 'LEGAL-001', 'LEGAL-006', 'CUST-001', 'REV-005', 'FIN-003', 'MGT-006', 'LEGAL-009', 'LEGAL-004', 'IP-009', 'REV-003', 'REV-007'],
    'Strategy Activity': ['STRAT-008', 'COMP-006', 'COMP-003', 'GROW-006', 'STRAT-004', 'IP-003', 'STRAT-002', 'STRAT-007', 'GROW-004', 'COMP-001', 'COMP-004', 'GROW-001', 'IP-005', 'STRAT-005', 'COMP-007', 'GROW-002', 'STRAT-009', 'COMP-008', 'GROW-005', 'STRAT-003', 'COMP-005', 'IP-006', 'GROW-008', 'STRAT-006', 'COMP-002'],
    'Efficiency Activity': ['OWNER-002', 'FIN-006', 'REV-008', 'GROW-007', 'OWNER-009', 'GROW-009', 'GROW-003', 'MGT-010', 'OWNER-008', 'REV-009', 'REV-004', 'REV-006', 'REV-002', 'FIN-010', 'MGT-007'],
    'Growth Activity': ['FIN-004', 'CUST-007', 'CUST-005', 'FIN-001', 'FIN-008', 'CUST-004', 'CUST-006'],
    'Culture Activity': ['MGT-001', 'MGT-003', 'OWNER-003', 'MGT-008', 'MGT-004', 'MGT-009', 'MGT-002']
  };

  const intangibleAssetMapping = {
    'Human': ['MGT-001', 'MGT-003', 'OWNER-003', 'MGT-006', 'OWNER-002', 'MGT-008', 'MGT-004', 'OWNER-008', 'IP-007', 'OWNER-004', 'MGT-005', 'MGT-002', 'LEGAL-005'],
    'Structural': ['IP-009', 'LEGAL-004', 'IP-003', 'OWNER-009', 'GROW-009', 'OWNER-007', 'GROW-003', 'MGT-010', 'IP-005', 'GROW-002', 'REV-009', 'IP-002', 'REV-004', 'REV-006', 'FIN-005', 'IP-001', 'GROW-005', 'REV-002', 'STRAT-003', 'FIN-010', 'LEGAL-007', 'COMP-005', 'MGT-009', 'LEGAL-008', 'IP-006', 'GROW-008', 'IP-004', 'MGT-007', 'FIN-007', 'LEGAL-002'],
    'Customer': ['CUST-007', 'REV-005', 'CUST-005', 'CUST-001', 'CUST-004', 'CUST-002', 'CUST-008', 'OWNER-006', 'STRAT-006', 'CUST-006', 'CUST-003', 'OWNER-001'],
    'Social': ['STRAT-008', 'COMP-006', 'COMP-003', 'STRAT-004', 'STRAT-002', 'STRAT-007', 'COMP-001', 'COMP-004', 'STRAT-005', 'COMP-007', 'STRAT-009', 'COMP-008', 'COMP-002'],
    'Revenue & Profitability': ['REV-007', 'REV-003', 'GROW-006', 'FIN-004', 'LEGAL-009', 'FIN-003', 'FIN-006', 'FIN-001', 'FIN-008', 'LEGAL-006', 'GROW-004', 'LEGAL-001', 'OWNER-010', 'REV-008', 'OWNER-005', 'GROW-007', 'GROW-001', 'REV-001', 'LEGAL-003', 'FIN-009', 'FIN-002']
  };

  // Calculate scores for activity types and asset types (using useMemo - MUST be before conditional returns)
  const calculateFilterScores = (mapping, responses) => {
    const scores = {};
    Object.keys(mapping).forEach(filterKey => {
      const questionIds = mapping[filterKey];
      const matchingResponses = responses.filter(q => questionIds.includes(q.question_id));
      const validResponses = matchingResponses.filter(r => r.answer_value !== 0);
      if (validResponses.length > 0) {
        const totalScore = validResponses.reduce((sum, r) => sum + (r.score || 0), 0);
        scores[filterKey] = totalScore / validResponses.length;
      } else {
        scores[filterKey] = 0;
      }
    });
    return scores;
  };

  const activityTypeScores = useMemo(() => calculateFilterScores(activityTypeMapping, answeredQuestions), [answeredQuestions]);
  const intangibleAssetScores = useMemo(() => calculateFilterScores(intangibleAssetMapping, answeredQuestions), [answeredQuestions]);

  // Prepare data for doughnut chart
  const gapDistributionData = useMemo(() => {
    if (!summary?.gap_distribution) return [];

    const distribution = summary.gap_distribution;
    return [
      { name: 'No Gaps', value: distribution.no_gaps, color: '#a07d5a' },
      { name: 'Minor Gaps', value: distribution.minor_gaps, color: '#6ba86e' },
      { name: 'Considerable Gaps', value: distribution.considerable_gaps, color: '#6ca5d6' },
      { name: 'Critical Gaps', value: distribution.critical_gaps, color: '#d4c563' },
      { name: 'Very Critical', value: distribution.very_critical, color: '#d9a066' },
      { name: 'Extremely Critical', value: distribution.extremely_critical, color: '#d97373' }
    ].filter(item => item.value > 0);
  }, [summary]);

  // Prepare data for readiness vs attractiveness chart
  const readinessAttractivenessData = useMemo(() => {
    if (!assessment) return [];
    return [{
      readiness: assessment.readiness_score || 0,
      attractiveness: assessment.attractiveness_score || 0
    }];
  }, [assessment]);

  // Check loading/error states AFTER all hooks have been called
  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading business gap assessment data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  // Compute remaining values that depend on data
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Category display names in proper order
  const categoryDisplayNames = {
    'financial_performance': 'Financial Performance & Documentation',
    'revenue_quality': 'Revenue Quality & Predictability',
    'customer_concentration': 'Customer Concentration & Relationships',
    'management_team': 'Management Team & Operations',
    'competitive_position': 'Competitive Position & Market Dynamic',
    'growth_potential': 'Growth Potential & Scalability',
    'intellectual_property': 'Intellectual Property & Proprietary Assets',
    'legal_compliance': 'Legal, Compliance & Risk Management',
    'owner_dependency': 'Owner Dependency & Transferability',
    'strategic_positioning': 'Strategic Positioning & Market Reputation',
  };

  const categoryScores = assessment?.category_scores || {};

  // Color coding functions matching the Business Gap Assessment page
  const getScoreColor = (score) => {
    if (score > 86) return '#a07d5a';      // Bronze/tan - No gaps
    if (score > 72) return '#6ba86e';      // Green - Minor gaps
    if (score > 57) return '#6ca5d6';      // Blue - Considerable gaps
    if (score > 43) return '#d4c563';      // Yellow - Critical gaps
    if (score > 28) return '#d9a066';      // Orange - Very critical gaps
    return '#d97373';                       // Red - Extremely critical
  };

  const getGapZoneColor = (gapLevel) => {
    const colors = {
      'No Gaps': '#a07d5a',
      'Minor Gaps': '#6ba86e',
      'Considerable Gaps': '#6ca5d6',
      'Critical Gaps': '#d4c563',
      'Very Critical': '#d9a066',
      'Extremely Critical': '#d97373'
    };
    return colors[gapLevel] || '#9ca3af';
  };

  const getCategoryBackgroundColor = (score) => {
    if (score > 86) return '#f5f3f0';      // Light bronze/tan
    if (score > 72) return '#f0f9f4';      // Light green
    if (score > 57) return '#f0f7fc';      // Light blue
    if (score > 43) return '#fefce8';      // Light yellow
    if (score > 28) return '#fef3e2';      // Light orange
    return '#fef2f2';                      // Light red
  };

  return (
    <div className="print-container" data-pdf-ready="true">
      {/* Cover Page */}
      <div className="print-page cover-page">
        <div className="cover-header">
          <h1>Business Gap Assessment Report</h1>
          <div className="report-type">EXIT READINESS AND ATTRACTIVENESS ANALYSIS</div>
        </div>

        <div className="cover-business-info">
          <h2>{business?.business_name || 'Business Name'}</h2>
          <p className="industry">{business?.industry || 'Industry'}</p>
        </div>

        <div className="cover-footer">
          <p>Generated: {today}</p>
          <p className="confidential">Confidential Business Assessment Report</p>
        </div>
      </div>

      {/* Executive Summary Page */}
      {summary && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <h1 className="section-title">Executive Summary</h1>

          {/* Overall Score and Readiness Level - Inline Layout */}
          <div style={{ display: 'flex', gap: '0.2in', marginBottom: '0.2in', flexWrap: 'wrap' }}>
            <div style={{ padding: '0.10in 0.12in', backgroundColor: 'transparent', borderRadius: '6px', borderLeft: '4px solid #1e3a8a', display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '2in' }}>
              <span style={{ fontSize: '8pt', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Overall Score:</span>
              <span style={{ fontSize: '11pt', fontWeight: '700', color: '#111827' }}>{summary.overall_score?.toFixed(1)}%</span>
            </div>
            <div style={{ padding: '0.10in 0.12in', backgroundColor: 'transparent', borderRadius: '6px', borderLeft: '4px solid #3b82f6', display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '2.2in' }}>
              <span style={{ fontSize: '8pt', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Readiness Level:</span>
              <span style={{ fontSize: '11pt', fontWeight: '700', color: '#111827' }}>{summary.readiness_level}</span>
            </div>
            <div style={{ padding: '0.10in 0.12in', backgroundColor: 'transparent', borderRadius: '6px', borderLeft: '4px solid #3b82f6', display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '2in' }}>
              <span style={{ fontSize: '8pt', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Questions Answered:</span>
              <span style={{ fontSize: '11pt', fontWeight: '700', color: '#111827' }}>{summary.answered_questions}</span>
            </div>
          </div>

          <div style={{ marginBottom: '0.10in' }}>
            <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.08in', color: '#1e3a8a' }}>Assessment Overview</h3>
            <p style={{ lineHeight: '1.5', color: '#374151', fontSize: '8pt' }}>
              {summary.readiness_description}
            </p>
          </div>

          {summary.cepa_interpretation?.executive_summary && (
            <div style={{ marginBottom: '0.10in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.08in', color: '#1e3a8a' }}>Executive Summary</h3>
              <p style={{ lineHeight: '1.5', color: '#374151', fontSize: '8pt', whiteSpace: 'pre-line' }}>
                {summary.cepa_interpretation.executive_summary}
              </p>
            </div>
          )}

          {/* Scoring Guide */}
          <div style={{ marginTop: '0.2in' }}>
            <h2 className="section-title">Scoring Guide</h2>

            {/* Scoring Guide Image */}
            <div style={{ marginBottom: '0.12in', textAlign: 'center' }}>
              <img
                src="/scoring-guide.png"
                alt="Scoring Guide"
                style={{ width: '90%', maxWidth: '100%', height: 'auto', borderRadius: '0px' }}
              />
            </div>

            <div style={{ marginBottom: '0.15in' }}>
              <h3 style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.08in', color: '#111827' }}>How Scores Are Calculated</h3>
              <p style={{ fontSize: '8pt', color: '#6b7280', lineHeight: '1.5' }}>
                Each question is scored on a scale that reflects the strength of that aspect of your business.
                The overall score is calculated by averaging all category scores, giving you a comprehensive
                view of your business readiness and attractiveness for maximum value.
              </p>
            </div>

            <div>
              <h3 style={{ fontSize: '9pt', fontWeight: '600', marginBottom: '0.08in', color: '#111827' }}>Score Ranges</h3>
              <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.08in', lineHeight: '1.5' }}>
                Based on the assessment scoring scale where average performance is around 58.5%:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.06in', marginBottom: '0.12in' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                  <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#c49e73', flexShrink: 0 }}></div>
                  <span style={{ fontSize: '8pt', color: '#374151' }}><strong>86-100%:</strong> Exceptional, best in class</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                  <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#a7d5a8', flexShrink: 0 }}></div>
                  <span style={{ fontSize: '8pt', color: '#374151' }}><strong>72-86%:</strong> Strong, minor improvements</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                  <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#b8d4e8', flexShrink: 0 }}></div>
                  <span style={{ fontSize: '8pt', color: '#374151' }}><strong>57-72%:</strong> Average, room for improvement</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                  <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#f4ebb0', flexShrink: 0 }}></div>
                  <span style={{ fontSize: '8pt', color: '#374151' }}><strong>43-57%:</strong> Below average, work needed</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                  <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#f5d7b3', flexShrink: 0 }}></div>
                  <span style={{ fontSize: '8pt', color: '#374151' }}><strong>28-43%:</strong> Poor, urgent attention required</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.05in', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                  <div style={{ width: '0.1in', height: '0.1in', borderRadius: '2px', backgroundColor: '#f5c9c9', flexShrink: 0 }}></div>
                  <span style={{ fontSize: '8pt', color: '#374151' }}><strong>0-28%:</strong> Critical deficiency</span>
                </div>
              </div>
              <div style={{ padding: '0.08in', backgroundColor: '#eff6ff', borderLeft: '3px solid #3b82f6', borderRadius: '4px' }}>
                <p style={{ fontSize: '8pt', color: '#1e3a8a', lineHeight: '1.4', margin: 0 }}>
                  <strong>Note:</strong> A score of 67% is the "green zone" target where businesses become much more attractive to buyers. A score of 72% or above is considered "best in class," representing clear, documented, and transferable processes.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visual Analysis Page - MOVED TO PAGE 3 */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <h1 className="section-title">Visual Analysis</h1>

        {/* Gap Distribution Doughnut Chart */}
        {gapDistributionData.length > 0 && (
          <div style={{ marginBottom: '0.3in' }}>
            <h2 style={{ fontSize: '11pt', fontWeight: '600', marginBottom: '0.15in', color: '#1e3a8a' }}>Gap Distribution</h2>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* Chart */}
              <div style={{ width: '2.8in', height: '2.8in', position: 'relative' }}>
                <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
                  {(() => {
                    const totalQuestions = gapDistributionData.reduce((sum, item) => sum + item.value, 0);
                    let currentAngle = -90;

                    return gapDistributionData.map((item, index) => {
                      const count = item.value;
                      if (count === 0) return null;

                      let sweepAngle = (count / totalQuestions) * 360;
                      if (sweepAngle >= 360) sweepAngle = 359.99;

                      const startAngle = currentAngle;
                      const endAngle = currentAngle + sweepAngle;
                      const largeArc = sweepAngle > 180 ? 1 : 0;

                      const baseOuterRadius = 80;
                      const baseInnerRadius = 50;

                      currentAngle = endAngle;

                      return (
                        <path
                          key={index}
                          d={`
                            M ${100 + baseInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + baseInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                            L ${100 + baseOuterRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + baseOuterRadius * Math.sin((startAngle * Math.PI) / 180)}
                            A ${baseOuterRadius} ${baseOuterRadius} 0 ${largeArc} 1 ${100 + baseOuterRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + baseOuterRadius * Math.sin((endAngle * Math.PI) / 180)}
                            L ${100 + baseInnerRadius * Math.cos((endAngle * Math.PI) / 180)} ${100 + baseInnerRadius * Math.sin((endAngle * Math.PI) / 180)}
                            A ${baseInnerRadius} ${baseInnerRadius} 0 ${largeArc} 0 ${100 + baseInnerRadius * Math.cos((startAngle * Math.PI) / 180)} ${100 + baseInnerRadius * Math.sin((startAngle * Math.PI) / 180)}
                            Z
                          `}
                          fill={item.color}
                          style={{ filter: 'brightness(1.1)' }}
                        />
                      );
                    });
                  })()}
                  <circle cx="100" cy="100" r="50" fill="white" />
                </svg>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                  <div style={{ fontSize: '29pt', fontWeight: '700', color: '#111827' }}>
                    {Math.round(assessment.overall_score || 0)}
                  </div>
                  <div style={{ fontSize: '8pt', fontWeight: '600', color: '#6b7280' }}>Score</div>
                </div>
              </div>

              {/* Legend */}
              <div style={{ width: '3.5in', paddingLeft: '0.3in' }}>
                {gapDistributionData.map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.08in', marginBottom: '0.06in', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ width: '0.12in', height: '0.12in', borderRadius: '2px', backgroundColor: item.color, marginRight: '0.08in' }}></div>
                      <span style={{ fontSize: '10pt', color: '#374151' }}>{item.name} ({
                        item.name === 'No Gaps' ? '>86%' :
                        item.name === 'Minor Gaps' ? '72-86%' :
                        item.name === 'Considerable Gaps' ? '57-72%' :
                        item.name === 'Critical Gaps' ? '43-57%' :
                        item.name === 'Very Critical' ? '28-43%' : '0-28%'
                      })</span>
                    </div>
                    <span style={{ fontSize: '10pt', fontWeight: '700', color: '#111827' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Readiness vs Attractiveness Scatter Chart & Impact Types */}
        {assessment && (
          <div style={{ marginTop: '0.3in', display: 'flex', gap: '0.25in' }}>
            {/* Left Side: Scatterplot */}
            <div style={{ flex: '1 1 55%' }}>
              <h2 style={{ fontSize: '11pt', fontWeight: '600', marginBottom: '0.08in', color: '#1e3a8a' }}>Readiness & Attractiveness</h2>
              <p style={{ fontSize: '9pt', color: '#6b7280', marginBottom: '0.12in' }}>Your business positioning for exit success</p>
              <div style={{ position: 'relative', width: '100%', height: '2.8in', backgroundColor: 'white', borderRadius: '8px', padding: '0.15in 0.1in 0.2in 0.25in', border: '1px solid #e5e7eb' }}>
                <svg viewBox="-12 -2 118 108" style={{ width: '100%', height: '100%' }} preserveAspectRatio="xMidYMid meet">
                <defs>
                  <linearGradient id="topRightGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 0.15 }} />
                    <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0.25 }} />
                  </linearGradient>
                  <linearGradient id="bottomLeftGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#ef4444', stopOpacity: 0.15 }} />
                    <stop offset="100%" style={{ stopColor: '#ef4444', stopOpacity: 0.2 }} />
                  </linearGradient>
                  <radialGradient id="pointGlow">
                    <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 0.8 }} />
                    <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0 }} />
                  </radialGradient>
                </defs>

                {/* Quadrants */}
                <rect x="50" y="0" width="50" height="50" fill="url(#topRightGradient)" />
                <rect x="0" y="50" width="50" height="50" fill="url(#bottomLeftGradient)" />

                {/* Grid lines */}
                {[20, 40, 60, 80].map(val => (
                  <React.Fragment key={val}>
                    <line x1="0" y1={100-val} x2="100" y2={100-val} stroke="#d1d5db" strokeWidth="0.4" strokeDasharray="3,3" />
                    <line x1={val} y1="0" x2={val} y2="100" stroke="#d1d5db" strokeWidth="0.4" strokeDasharray="3,3" />
                  </React.Fragment>
                ))}

                {/* Center crosshair */}
                <line x1="0" y1="50" x2="100" y2="50" stroke="#6b7280" strokeWidth="1" strokeDasharray="5,4" />
                <line x1="50" y1="0" x2="50" y2="100" stroke="#6b7280" strokeWidth="1" strokeDasharray="5,4" />

                {/* Axes */}
                <line x1="0" y1="100" x2="100" y2="100" stroke="#1f2937" strokeWidth="1.5" />
                <line x1="0" y1="0" x2="0" y2="100" stroke="#1f2937" strokeWidth="1.5" />

                {/* Arrow heads */}
                <polygon points="100,100 96,98.5 96,101.5" fill="#1f2937" />
                <polygon points="0,0 1.5,4 -1.5,4" fill="#1f2937" />

                {/* Data point */}
                {assessment.overall_score > 0 && (
                  <g>
                    <circle
                      cx={assessment.readiness_score || 20}
                      cy={100 - (assessment.attractiveness_score || 20)}
                      r="10"
                      fill="url(#pointGlow)"
                    />
                    <circle
                      cx={assessment.readiness_score || 20}
                      cy={100 - (assessment.attractiveness_score || 20)}
                      r="6"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="1"
                      opacity="0.5"
                    />
                    <circle
                      cx={assessment.readiness_score || 20}
                      cy={100 - (assessment.attractiveness_score || 20)}
                      r="4"
                      fill="#3b82f6"
                      stroke="white"
                      strokeWidth="2"
                    />
                    <circle
                      cx={assessment.readiness_score || 20}
                      cy={100 - (assessment.attractiveness_score || 20)}
                      r="1.8"
                      fill="white"
                      opacity="0.9"
                    />
                    {/* Crosshair lines */}
                    <line
                      x1={assessment.readiness_score || 20}
                      y1={100 - (assessment.attractiveness_score || 20)}
                      x2={assessment.readiness_score || 20}
                      y2="100"
                      stroke="#3b82f6"
                      strokeWidth="0.8"
                      strokeDasharray="3,3"
                      opacity="0.6"
                    />
                    <line
                      x1="0"
                      y1={100 - (assessment.attractiveness_score || 20)}
                      x2={assessment.readiness_score || 20}
                      y2={100 - (assessment.attractiveness_score || 20)}
                      stroke="#3b82f6"
                      strokeWidth="0.8"
                      strokeDasharray="3,3"
                      opacity="0.6"
                    />
                    {/* Score labels */}
                    <text
                      x={assessment.readiness_score || 20}
                      y={100 - (assessment.attractiveness_score || 20) - 13}
                      fontSize="10"
                        fill="#4f46e5"
                      textAnchor="middle"
                      fontWeight="700"
                    >
                      R: {Math.round(assessment.readiness_score || 0)}% | A: {Math.round(assessment.attractiveness_score || 0)}%
                    </text>
                  </g>
                )}

                {/* Axis labels */}
                {[0, 25, 50, 75, 100].map(val => (
                  <React.Fragment key={`label-${val}`}>
                    <text x={val} y="105" fontSize="6" fill="#374151" textAnchor="middle" fontWeight="600">{val}</text>
                    <text x="-5" y={102-val} fontSize="6" fill="#374151" textAnchor="end" fontWeight="600">{val}</text>
                  </React.Fragment>
                ))}
              </svg>

              {/* Axis titles */}
              <div style={{ position: 'absolute', bottom: '0.05in', left: '0.25in', right: '0.1in', textAlign: 'center' }}>
                <p style={{ fontSize: '8pt', fontWeight: '700', color: '#374151', margin: 0 }}>Exit Readiness →</p>
              </div>
              <div style={{ position: 'absolute', left: '.2in', top: '0.15in', bottom: '0.2in', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '0.8in' }}>
                <div style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}>
                  <p style={{ fontSize: '8pt', fontWeight: '700', color: '#374151', margin: 0, whiteSpace: 'nowrap' }}>Business Attractiveness →</p>
                </div>
              </div>
              </div>

              {/* Legend */}
              <div style={{ marginTop: '0.12in', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1in', fontSize: '8pt' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.06in', backgroundColor: 'white', borderRadius: '4px', border: '2px solid #bbf7d0' }}>
                  <div style={{ width: '0.12in', height: '0.12in', borderRadius: '2px', backgroundColor: '#dcfce7', border: '2px solid #86efac' }}></div>
                  <span style={{ color: '#374151' }}><strong>Ideal Zone:</strong> High R & A</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.06in', padding: '0.06in', backgroundColor: 'white', borderRadius: '4px', border: '2px solid #fecaca' }}>
                  <div style={{ width: '0.12in', height: '0.12in', borderRadius: '2px', backgroundColor: '#fee2e2', border: '2px solid #fca5a5' }}></div>
                  <span style={{ color: '#374151' }}><strong>Critical Zone:</strong> Needs improvement</span>
                </div>
              </div>
            </div>

            {/* Right Side: Impact Type Analysis */}
            <div style={{ flex: '1 1 45%' }}>
              <h2 style={{ fontSize: '11pt', fontWeight: '600', marginBottom: '0.08in', color: '#1e3a8a' }}>Impact Type Analysis</h2>
              <p style={{ fontSize: '9pt', color: '#6b7280', marginBottom: '0.12in' }}>Key dimensions influencing exit readiness</p>

              {/* Readiness Score */}
              <div style={{
                padding: '0.15in',
                backgroundColor: getScoreColor(assessment.readiness_score || 0) + '20',
                border: `3px solid ${getScoreColor(assessment.readiness_score || 0)}`,
                borderRadius: '6px',
                marginBottom: '0.15in'
              }}>
                <div style={{ fontSize: '8pt', fontWeight: '600', color: '#6b7280', marginBottom: '0.06in', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Readiness Score
                </div>
                <div style={{ fontSize: '19pt', fontWeight: '700', color: getScoreColor(assessment.readiness_score || 0), marginBottom: '0.08in' }}>
                  {Math.round(assessment.readiness_score || 0)}%
                </div>
                <div style={{ fontSize: '8pt', color: '#6b7280', lineHeight: '1.3' }}>
                  Measures operational maturity, systems documentation, and transferability of your business operations.
                </div>
              </div>

              {/* Attractiveness Score */}
              <div style={{
                padding: '0.15in',
                backgroundColor: getScoreColor(assessment.attractiveness_score || 0) + '20',
                border: `3px solid ${getScoreColor(assessment.attractiveness_score || 0)}`,
                borderRadius: '6px',
                marginBottom: '0.15in'
              }}>
                <div style={{ fontSize: '8pt', fontWeight: '600', color: '#6b7280', marginBottom: '0.06in', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Attractiveness Score
                </div>
                <div style={{ fontSize: '19pt', fontWeight: '700', color: getScoreColor(assessment.attractiveness_score || 0), marginBottom: '0.08in' }}>
                  {Math.round(assessment.attractiveness_score || 0)}%
                </div>
                <div style={{ fontSize: '8pt', color: '#6b7280', lineHeight: '1.3' }}>
                  Measures market position, competitive advantages, growth potential, and overall appeal to potential buyers.
                </div>
              </div>

              {/* Explanation */}
              <div style={{ padding: '0.1in', backgroundColor: '#f3f4f6', borderRadius: '4px', borderLeft: '3px solid #3b82f6' }}>
                <p style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.3', margin: 0 }}>
                  <strong>Readiness</strong> focuses on internal preparedness for transition. <strong>Attractiveness</strong> measures external appeal to buyers.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Business Assessment Categories - PAGE 4 */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>
        
        <div style={{ marginBottom: '0.12in', padding: '0.12in', backgroundColor: '#ffffff', borderRadius: '6px', borderLeft: '4px solid #1d4ed8' }}>
          <h2 style={{ fontSize: '17pt', fontWeight: '800', marginBottom: '0.15in', color: '#1d4ed8' }}>Business Assessment</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15in' }}>
            {[
              { key: 'financial_performance', name: 'Financial Performance & Documentation' },
              { key: 'revenue_quality', name: 'Revenue Quality & Predictability' },
              { key: 'customer_concentration', name: 'Customer Concentration & Relationships' },
              { key: 'management_team', name: 'Management Team & Operations' },
              { key: 'competitive_position', name: 'Competitive Position & Market Dynamic' },
              { key: 'growth_potential', name: 'Growth Potential & Scalability' },
              { key: 'intellectual_property', name: 'Intellectual Property & Proprietary Assets' },
              { key: 'legal_compliance', name: 'Legal, Compliance & Risk Management' },
              { key: 'owner_dependency', name: 'Owner Dependency & Transferability' },
              { key: 'strategic_positioning', name: 'Strategic Positioning & Market Reputation' }
            ].map(({ key, name }) => {
              const score = categoryScores[key];
              const displayScore = score !== null && score !== undefined ? score : 0;
              const scoreColor = getScoreColor(displayScore);
              const bgColor = getCategoryBackgroundColor(displayScore);

              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '0.08in 0.12in', backgroundColor: bgColor, borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                  <div style={{ flex: 1, minWidth: 0, paddingRight: '0.15in' }}>
                    <span style={{ fontSize: '10pt', fontWeight: '600', color: '#111827' }}>{name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '1.8in' }}>
                    <span style={{ fontSize: '10pt', fontWeight: '700', color: scoreColor, minWidth: '0.4in', textAlign: 'right' }}>
                      {score !== null && score !== undefined ? `${score.toFixed(1)}%` : 'N/A'}
                    </span>
                    <div style={{ width: '1in', height: '0.08in', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: `${displayScore}%`, height: '100%', backgroundColor: scoreColor, borderRadius: '4px' }}></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Family Enterprise Categories - PAGE 5 */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ marginBottom: '0.12in', padding: '0.12in', backgroundColor: '#ffffff', borderRadius: '6px', borderLeft: '4px solid #7e22ce' }}>
          <h2 style={{ fontSize: '17pt', fontWeight: '800', marginBottom: '0.15in', color: '#7e22ce' }}>Family Enterprise</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15in' }}>
            {[
            { key: 'family_governing', name: 'Family Governance & Decision-Making Structure' },
            { key: 'succession_planning', name: 'Succession Planning & Next-Generation Readiness' },
            { key: 'family_conflict', name: 'Family Communication & Conflict Resolution' },
            { key: 'equity_planning', name: 'Ownership Structure & Equity Planning' },
            { key: 'employment_policies', name: 'Family Employment & Compensation Policies' },
            { key: 'family_council', name: 'Family Council & Board Effectiveness' },
            { key: 'wealth_transfer', name: 'Wealth Transfer & Estate Integration' },
            { key: 'values_mission', name: 'Family Values, Mission & Legacy Preservation' },
            { key: 'education_development', name: 'Education & Development of Rising Generation' },
            { key: 'boundary_management', name: 'Family-Business Boundary Management' }
            ].map(({ key, name }) => {
              const score = categoryScores[key];
              const displayScore = score !== null && score !== undefined ? score : 0;
              const scoreColor = getScoreColor(displayScore);
              const bgColor = getCategoryBackgroundColor(displayScore);

              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '0.08in 0.12in', backgroundColor: bgColor, borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                  <div style={{ flex: 1, minWidth: 0, paddingRight: '0.15in' }}>
                    <span style={{ fontSize: '10pt', fontWeight: '600', color: '#111827' }}>{name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.1in', minWidth: '1.8in' }}>
                    <span style={{ fontSize: '10pt', fontWeight: '700', color: scoreColor, minWidth: '0.4in', textAlign: 'right' }}>
                      {score !== null && score !== undefined ? `${score.toFixed(1)}%` : 'N/A'}
                    </span>
                    <div style={{ width: '1in', height: '0.08in', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: `${displayScore}%`, height: '100%', backgroundColor: scoreColor, borderRadius: '4px' }}></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Activity Types & Asset Types Page - PAGE 6 */}
      <div className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <h1 className="section-title">Activity & Asset Type Analysis</h1>

        {/* Activity Types */}
        <div style={{ marginBottom: '0.25in' }}>
          <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.12in', lineHeight: '1.4' }}>
            Activity types categorize questions based on task completion priority and the strategic function they serve in your business.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1in' }}>
            {Object.entries({
              'De-Risking Activity': 'Questions that reduce business risk and protect value. These activities should typically be completed first as they prevent value destruction.',
              'Strategy Activity': 'Questions related to long-term planning, market positioning, and competitive advantage that define the direction of your business.',
              'Efficiency Activity': 'Questions about operational optimization, process improvement, and resource utilization that make your business run smoother.',
              'Growth Activity': 'Questions focused on expansion, new markets, and scaling operations that drive top-line revenue.',
              'Culture Activity': 'Questions about organizational values, team dynamics, and workplace environment that create the foundation for sustainable performance.'
            }).map(([activityType, description]) => {
              const score = activityTypeScores[activityType] || 0;
              const color = getScoreColor(score);
              return (
                <div
                  key={activityType}
                  style={{
                    padding: '0.08in',
                    backgroundColor: color + '30',
                    border: `2px solid ${color}`,
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.04in' }}>
                    <div style={{ fontSize: '8pt', fontWeight: '700', color: '#111827' }}>
                      {activityType}
                    </div>
                    <div style={{ fontSize: '11pt', fontWeight: '700', color: color }}>
                      {score.toFixed(1)}%
                    </div>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.3' }}>
                    {description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Intangible Asset Types */}
        <div>
          <h2 style={{ fontSize: '11pt', fontWeight: '600', marginBottom: '0.1in', color: '#1e3a8a' }}>Intangible Asset Types</h2>
          <p style={{ fontSize: '8pt', color: '#6b7280', marginBottom: '0.12in', lineHeight: '1.4' }}>
            Intangible asset types represent the non-physical resources that create value in your business and directly impact your exit valuation.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.1in' }}>
            {Object.entries({
              'Human': 'The collective skills, knowledge, and capabilities of your workforce including employee expertise, training programs, and leadership quality.',
              'Structural': 'The systems, processes, databases, and intellectual property that remain with the company including proprietary software and documented procedures.',
              'Customer': 'The value of your customer relationships, brand reputation, and market position including customer loyalty and brand equity.',
              'Social': 'The value derived from networks, partnerships, and external relationships including strategic alliances and industry connections.',
              'Revenue & Profitability': 'The financial performance and earnings capacity of your business including revenue streams, profit margins, and financial stability.'
            }).map(([assetType, description]) => {
              const score = intangibleAssetScores[assetType] || 0;
              const color = getScoreColor(score);
              return (
                <div
                  key={assetType}
                  style={{
                    padding: '0.08in',
                    backgroundColor: color + '30',
                    border: `2px solid ${color}`,
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.04in' }}>
                    <div style={{ fontSize: '8pt', fontWeight: '700', color: '#111827' }}>
                      {assetType}
                    </div>
                    <div style={{ fontSize: '11pt', fontWeight: '700', color: color }}>
                      {score.toFixed(1)}%
                    </div>
                  </div>
                  <div style={{ fontSize: '8pt', color: '#374151', lineHeight: '1.3' }}>
                    {description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detailed Analysis Page */}
      {summary?.cepa_interpretation && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <h1 className="section-title">Detailed Analysis</h1>

          {summary.cepa_interpretation.value_drivers && (
            <div style={{ marginBottom: '0.3in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in', color: '#16a34a' }}>Value Drivers</h3>
              <p style={{ lineHeight: '1.6', color: '#6b7280', fontSize: '9pt' }}>
                {summary.cepa_interpretation.value_drivers}
              </p>
            </div>
          )}

          {summary.cepa_interpretation.risk_factors && (
            <div style={{ marginBottom: '0.3in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in', color: '#ea580c' }}>Risk Factors</h3>
              <p style={{ lineHeight: '1.6', color: '#6b7280', fontSize: '9pt' }}>
                {summary.cepa_interpretation.risk_factors}
              </p>
            </div>
          )}

          {summary.cepa_interpretation.transferability_analysis && (
            <div style={{ marginBottom: '0.3in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in', color: '#9333ea' }}>Transferability Analysis</h3>
              <p style={{ lineHeight: '1.6', color: '#6b7280', fontSize: '9pt' }}>
                {summary.cepa_interpretation.transferability_analysis}
              </p>
            </div>
          )}

          {summary.cepa_interpretation.market_positioning && (
            <div style={{ marginBottom: '0.3in' }}>
              <h3 style={{ fontSize: '10pt', fontWeight: '600', marginBottom: '0.1in', color: '#4f46e5' }}>Market Positioning</h3>
              <p style={{ lineHeight: '1.6', color: '#6b7280', fontSize: '9pt' }}>
                {summary.cepa_interpretation.market_positioning}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Recommendations Page */}
      {summary?.recommendations && summary.recommendations.length > 0 && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <h1 className="section-title">Strategic Recommendations</h1>

          <div className="next-steps">
            {summary.recommendations.map((rec, idx) => {
              // Dynamic colors based on priority level
              const priorityColors = rec.priority === 'Immediate'
                ? { background: '#fee2e2', color: '#991b1b' }  // Red
                : rec.priority === 'High'
                ? { background: '#fed7aa', color: '#9a3412' }  // Orange
                : { background: '#dbeafe', color: '#1e3a8a' }; // Blue (Medium)

              return (
                <div key={idx} className="step">
                  <h3>{rec.title}</h3>
                  <div style={{
                    display: 'inline-block',
                    padding: '0.03in 0.08in',
                    background: priorityColors.background,
                    borderRadius: '4px',
                    fontSize: '8pt',
                    fontWeight: '600',
                    marginBottom: '0.06in',
                    color: priorityColors.color
                  }}>
                    Priority: {rec.priority}
                  </div>
                  <p>{rec.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next Steps Page */}
      {summary?.next_steps && summary.next_steps.length > 0 && (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <h1 className="section-title">Next Steps</h1>

          <div className="next-steps">
            {summary.next_steps.map((step, idx) => (
              <div key={idx} className="step">
                <h3>Step {step.step}: {step.action}</h3>
                <p>{step.description}</p>
                <p style={{ fontSize: '8pt', color: '#6b7280', fontStyle: 'italic', marginTop: '0.05in' }}>
                  <strong>Timeframe:</strong> {step.timeframe}
                </p>
              </div>
            ))}
          </div>

          <div className="disclosure-box" style={{ marginTop: '0.4in' }}>
            <h3>Important Note</h3>
            <p>
              This assessment provides a comprehensive analysis of your business's exit readiness based on industry best practices
              and CEPA (Certified Exit Planning Advisor) standards. The recommendations are tailored to your specific situation
              and should be reviewed with your advisory team.
            </p>
            <p>
              <strong>Contact Dominion Business Advisors to discuss your personalized action plan and begin your exit planning journey.</strong>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintBusinessGapAssessment;
